import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button, Form } from 'react-bootstrap';
import orderRepository from '../../../services/repositories/order.repository';
import productRepository from '../../../services/repositories/product.repository';
import userRepository from '../../../services/repositories/user.repository';
import '../../../styles/products.scss';

const AddOrder = ({ isEdit = false, orderId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    serial_name: '',
    product_id: '',
    user_id: '',
    status: 'pending',
    payment_status: 'pending',
    delivery_address: '',
    delivery_latitude: '',
    delivery_longitude: '',
    pack_id: '',
    note: '',
  });
  
  const [originalStatus, setOriginalStatus] = useState('pending'); // Track original status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchUsers();
    if (isEdit && orderId) {
      fetchOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, orderId]);

  const fetchProducts = async () => {
    try {
      const response = await productRepository.getAll();
      const productsData = response.data || response;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userRepository.getAll();
      const usersData = response.data || response;
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchOrderData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await orderRepository.getById(orderId);
      const order = response.data || response;
      
      if (!order || !order.id) {
        throw new Error('Order data not found');
      }
      
      setFormData({
        serial_name: order.serial_name || '',
        product_id: order.product_id || order.product?.id || '',
        user_id: order.user_id || order.user?.id || '',
        status: order.status || 'pending',
        payment_status: order.payment_status || 'pending',
        delivery_address: order.delivery_address || '',
        delivery_latitude: order.delivery_latitude || '',
        delivery_longitude: order.delivery_longitude || '',
        pack_id: order.pack_id || '',
        note: order.note || '',
      });
      setOriginalStatus(order.status || 'pending'); // Store original status
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Error loading order data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submitData = {
        serial_name: formData.serial_name || '',
        product_id: formData.product_id || null,
        user_id: formData.user_id || null,
        status: formData.status || 'pending',
        payment_status: formData.payment_status || 'pending',
        delivery_address: formData.delivery_address || null,
        delivery_latitude: formData.delivery_latitude ? parseFloat(formData.delivery_latitude) : null,
        delivery_longitude: formData.delivery_longitude ? parseFloat(formData.delivery_longitude) : null,
        pack_id: formData.pack_id || null,
        note: formData.note || null,
      };
      
      if (isEdit && orderId) {
        // Check if status changed - use updateStatus endpoint for stock changes
        if (formData.status !== originalStatus) {
          await orderRepository.updateStatus(orderId, formData.status);
        }
        // Update other fields
        await orderRepository.update(orderId, submitData);
      } else {
        await orderRepository.create(submitData);
      }
      
      navigate('/admin/orders/orders-list');
    } catch (err) {
      console.error('Error saving order:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde de la commande');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/orders/orders-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données de la commande...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>
            {isEdit ? 'Modifier la Commande' : 'Nouvelle Commande'}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="serial_name" className="form-label">
                    Numéro de Série <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="serial_name"
                    name="serial_name"
                    className="form-control"
                    placeholder="Numéro de série"
                    value={formData.serial_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="product_id" className="form-label">
                    Produit
                  </label>
                  <select
                    className="form-control"
                    id="product_id"
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un produit</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="user_id" className="form-label">
                    Client
                  </label>
                  <select
                    className="form-control"
                    id="user_id"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un client</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="status" className="form-label">
                    Statut
                  </label>
                  <select
                    className="form-control"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="pending">En attente</option>
                    <option value="processing">En traitement</option>
                    <option value="shipped">Expédié</option>
                    <option value="delivered">Livré</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="payment_status" className="form-label">
                    Statut de Paiement
                  </label>
                  <select
                    className="form-control"
                    id="payment_status"
                    name="payment_status"
                    value={formData.payment_status}
                    onChange={handleInputChange}
                  >
                    <option value="pending">En attente</option>
                    <option value="paid">Payé</option>
                    <option value="failed">Échoué</option>
                    <option value="refunded">Remboursé</option>
                  </select>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="pack_id" className="form-label">
                    ID Pack
                  </label>
                  <input
                    type="number"
                    id="pack_id"
                    name="pack_id"
                    className="form-control"
                    placeholder="ID du pack (optionnel)"
                    value={formData.pack_id}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="delivery_address" className="form-label">
                    Adresse de Livraison
                  </label>
                  <input
                    type="text"
                    id="delivery_address"
                    name="delivery_address"
                    className="form-control"
                    placeholder="Adresse de livraison"
                    value={formData.delivery_address}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="delivery_latitude" className="form-label">
                    Latitude de Livraison
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="delivery_latitude"
                    name="delivery_latitude"
                    className="form-control"
                    placeholder="Latitude"
                    value={formData.delivery_latitude}
                    onChange={handleInputChange}
                    min="-90"
                    max="90"
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="delivery_longitude" className="form-label">
                    Longitude de Livraison
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="delivery_longitude"
                    name="delivery_longitude"
                    className="form-control"
                    placeholder="Longitude"
                    value={formData.delivery_longitude}
                    onChange={handleInputChange}
                    min="-180"
                    max="180"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="note" className="form-label">
                    Note
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    className="form-control"
                    rows="3"
                    placeholder="Note (optionnel)"
                    value={formData.note}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            
            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer la Commande')}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddOrder;
